import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { WalletLink } from "../components/WalletLink";
import { StatsCard } from "../components/StatsCard";
import { TotalPointsChart } from "../components/TotalPointsChart";
import { useAirdrops } from "../hooks/useAirdrops";
import { formatCards } from "../utils/format";
import { Skeleton } from "../components/Skeleton";

export function Dashboard() {
  // Fetch stats summary
  const statsQuery = useQuery({
    queryKey: ["stats-summary"],
    queryFn: async () => {
      const { data, error } = await api.stats.get();
      if (error) throw error;
      return data;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch fresh spinners
  const freshSpinnersQuery = useQuery({
    queryKey: ["fresh-spinners"],
    queryFn: async () => {
      const { data, error } = await api.freshSpinners.get({
        query: { limit: 10 },
      });
      if (error) throw error;
      return data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch recent prize events
  const prizeEventsQuery = useQuery({
    queryKey: ["new-winner-events"],
    queryFn: async () => {
      const { data, error } = await api.newWinnerEvents.get({
        query: { page: 1, limit: 10 },
      });
      if (error) throw error;
      return data;
    },
  });

  // Fetch top points leaderboard
  const leaderboardQuery = useQuery({
    queryKey: ["leaderboard-points"],
    queryFn: async () => {
      const { data, error } = await api.leaderboard.points.get({
        query: { limit: 5 },
      });
      if (error) throw error;
      return data;
    },
  });

  const stats = statsQuery.data?.data;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="page-title">Overview</h1>
        <p className="section-hint mt-1">
          What's happening across Collector Crypt's gacha machines right now.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total wallets"
          value={stats?.totalWallets?.toLocaleString() ?? "–"}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          }
          color="blue"
        />
        <StatsCard
          title="Active last 24h"
          value={stats?.activeWallets24h?.toLocaleString() ?? "–"}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          }
          color="green"
        />
        <StatsCard
          title="Prizes won last 24h"
          value={stats?.prizesWon24h?.toLocaleString() ?? "–"}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
              />
            </svg>
          }
          color="purple"
        />
        <StatsCard
          title="Prize value last 24h"
          value={stats ? `$${(stats.prizeValue24h / 1000).toFixed(1)}k` : "–"}
          icon={
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="yellow"
        />
      </div>

      {/* Fresh Spinners Section */}
      <section className="card">
        <div className="flex items-center justify-between gap-4 mb-1">
          <h2 className="section-title flex items-center gap-2.5">
            <span className="live-dot" />
            Fresh spinners
          </h2>
          <Link
            to="/fresh-spinners"
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            View all
          </Link>
        </div>
        <p className="section-hint mb-4">
          New wallets with a burst of activity: 3+ wins in the last 72 hours.
        </p>

        {freshSpinnersQuery.isLoading && (
          <Skeleton kind="table" rows={5} />
        )}
        {freshSpinnersQuery.error && (
          <p className="text-red-400">
            Couldn't load fresh spinners. {String(freshSpinnersQuery.error)}
          </p>
        )}
        {freshSpinnersQuery.data?.data &&
          freshSpinnersQuery.data.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="table-head">
                    <th className="pb-2 pr-4 font-medium">Wallet</th>
                    <th className="pb-2 pr-4 font-medium">Age</th>
                    <th className="pb-2 pr-4 font-medium">Wins</th>
                    <th className="pb-2 pr-4 font-medium">Points velocity</th>
                    <th className="pb-2 pr-4 font-medium">Score</th>
                    <th className="pb-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {freshSpinnersQuery.data.data.map((spinner) => (
                    <tr key={spinner.walletAddress} className="table-row">
                      <td className="py-2 pr-4">
                        <WalletLink address={spinner.walletAddress} />
                      </td>
                      <td className="py-2 pr-4 num text-gray-300">
                        {spinner.hoursOld.toFixed(1)}h
                      </td>
                      <td className="py-2 pr-4 num text-gray-300">
                        {spinner.winEventCount}
                      </td>
                      <td className="py-2 pr-4 num text-gray-300">
                        {spinner.pointsVelocity !== null
                          ? `${spinner.pointsVelocity.toLocaleString()}/h`
                          : "–"}
                      </td>
                      <td className="py-2 pr-4 num text-gray-50 font-medium">
                        {spinner.activityScore.toFixed(1)}
                      </td>
                      <td className="py-2">
                        {spinner.hasActiveSession ? (
                          <span className="pill bg-green-400/15 text-green-400">
                            <span className="live-dot scale-75" />
                            Active
                          </span>
                        ) : (
                          <span className="pill bg-gray-700/70 text-gray-400">
                            Idle
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        {freshSpinnersQuery.data?.data?.length === 0 && (
          <p className="text-gray-500">
            No fresh spinners right now. New wallets show up here as soon as they start
            winning. Meanwhile, the{" "}
            <Link to="/machines" className="text-blue-400 hover:text-blue-300">
              machines page
            </Link>{" "}
            shows where the value is today.
          </p>
        )}
      </section>

      {/* Two-column layout for leaderboard and recent events */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Points Leaderboard */}
        <section className="card">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="section-title">Top points</h2>
            <Link
              to="/leaderboards"
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Full leaderboard
            </Link>
          </div>
          {leaderboardQuery.isLoading && (
            <Skeleton kind="table" rows={5} />
          )}
          {leaderboardQuery.error && (
            <p className="text-red-400">
              Couldn't load the leaderboard. {String(leaderboardQuery.error)}
            </p>
          )}
          {leaderboardQuery.data && (
            <ol className="space-y-1">
              {leaderboardQuery.data.data.map((wallet, index) => (
                <li
                  key={wallet.id}
                  className="flex items-center justify-between gap-3 py-2 border-b border-gray-700/60 last:border-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      className={`num w-6 h-6 shrink-0 flex items-center justify-center rounded-full text-xs font-semibold ${
                        index === 0
                          ? "bg-yellow-400/15 text-yellow-400"
                          : "bg-gray-700/70 text-gray-400"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <WalletLink address={wallet.walletAddress} />
                  </div>
                  <span className="num text-gray-200 font-medium whitespace-nowrap">
                    {wallet.totalPoints?.toLocaleString()} pts
                  </span>
                </li>
              ))}
            </ol>
          )}
          {leaderboardQuery.data?.data.length === 0 && (
            <p className="text-gray-500">
              No wallet points recorded yet. Search for a wallet above to start tracking it.
            </p>
          )}
        </section>

        {/* Recent Prize Events */}
        <section className="card">
          <h2 className="section-title mb-4">Recent prizes</h2>
          {prizeEventsQuery.isLoading && (
            <Skeleton kind="table" rows={8} />
          )}
          {prizeEventsQuery.error && (
            <p className="text-red-400">
              Couldn't load recent prizes. {String(prizeEventsQuery.error)}
            </p>
          )}
          {prizeEventsQuery.data && (
            <ul className="space-y-1">
              {prizeEventsQuery.data.data.map((event) => (
                <li
                  key={event.id}
                  className="flex items-center justify-between gap-3 py-2 border-b border-gray-700/60 last:border-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-100 truncate">{event.nftName}</div>
                    <div className="text-sm text-gray-400">
                      won by <WalletLink address={event.winner} />
                    </div>
                  </div>
                  <span className="num text-gray-50 font-medium whitespace-nowrap">
                    ${event.insuredValue?.toFixed(0)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {prizeEventsQuery.data?.data.length === 0 && (
            <p className="text-gray-500">
              No prizes recorded yet. Start the Ably listener to collect them.
            </p>
          )}
        </section>
      </div>

      {/* Latest $CARDS airdrop */}
      <LatestAirdropSection />

      {/* Total Platform Points Chart */}
      <TotalPlatformPointsSection />
    </div>
  );
}

type ChartRange = "7d" | "30d" | "90d" | "all";

function TotalPlatformPointsSection() {
  const [chartRange, setChartRange] = useState<ChartRange>("30d");

  const getRangeDates = (range: ChartRange) => {
    const to = new Date().toISOString();
    if (range === "all") return { from: undefined, to: undefined };
    const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
    const from = new Date(
      Date.now() - days * 24 * 60 * 60 * 1000
    ).toISOString();
    return { from, to };
  };

  const { from, to } = getRangeDates(chartRange);

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="section-title">Total platform points</h2>
        <div className="seg">
          {(["7d", "30d", "90d", "all"] as ChartRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setChartRange(range)}
              className={`seg-btn ${chartRange === range ? "seg-btn-on" : ""}`}
            >
              {range === "all" ? "All" : range}
            </button>
          ))}
        </div>
      </div>
      <TotalPointsChart from={from} to={to} />
    </section>
  );
}

function LatestAirdropSection() {
  const airdropsQuery = useAirdrops();
  const latest = airdropsQuery.data?.data.find((a) => a.recipientCount > 0);

  if (!latest) return null;

  const avgPerRecipient = latest.totalDistributed / latest.recipientCount;

  return (
    <section className="card">
      <div className="flex items-center justify-between gap-4 mb-4">
        <h2 className="section-title flex items-center gap-2">
          <svg
            className="w-5 h-5 text-yellow-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Latest $CARDS airdrop: {latest.label}
        </h2>
        <Link
          to="/airdrops"
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          All airdrops
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">$CARDS distributed</div>
          <div className="num text-xl font-semibold text-yellow-400">
            {formatCards(latest.totalDistributed)}
          </div>
        </div>
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">Recipients</div>
          <div className="num text-xl font-semibold text-gray-50">
            {latest.recipientCount.toLocaleString()}
          </div>
        </div>
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">Average per recipient</div>
          <div className="num text-xl font-semibold text-gray-50">
            {formatCards(avgPerRecipient)}
          </div>
        </div>
        <div className="card-inset p-3">
          <div className="text-gray-400 text-xs mb-1">Last indexed</div>
          <div className="num text-xl font-semibold text-gray-200">
            {latest.lastSyncedAt
              ? new Date(latest.lastSyncedAt).toLocaleDateString()
              : "–"}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        Points period{" "}
        {new Date(latest.periodFrom).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" })}
        {" – "}
        {new Date(new Date(latest.periodTo).getTime() - 1).toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" })}
        . Open a wallet to see what it received and how similar-points wallets compare.
      </p>
    </section>
  );
}
